/**
 * Basic Info Fields - Name, Type, Brand, Model
 */

import React from 'react';
import { Flex, Box, Text, TextField, Select } from '@radix-ui/themes';
import { DeviceTemplate, DeviceType } from '../../types';
import { DEVICE_TYPE_LABELS } from '../../config/constants';
import DeviceIcon from '../DeviceIcon';

interface BasicInfoFieldsProps {
  template: Partial<DeviceTemplate>;
  onChange: (updated: Partial<DeviceTemplate>) => void;
  showTypeSelector?: boolean;
}

const BasicInfoFields: React.FC<BasicInfoFieldsProps> = ({ 
  template, 
  onChange,
  showTypeSelector = true 
}) => {
  return (
    <>
      <Flex gap="3">
        <Box style={{ flex: 2 }}>
          <Text as="label" size="2" weight="bold" htmlFor="templateName">
            Device Name *
          </Text>
          <TextField.Root
            id="templateName"
            value={template.name || ''}
            onChange={(e) => onChange({ ...template, name: e.target.value })}
            placeholder="e.g., Main Mount"
            required
            mt="1"
          />
        </Box>

        {showTypeSelector && (
          <Box style={{ flex: 1 }}>
            <Text as="label" size="2" weight="bold" htmlFor="templateType">
              Type *
            </Text>
            <Select.Root 
              value={template.type || 'other'} 
              onValueChange={(value) => onChange({ ...template, type: value as DeviceType })}
            >
              <Select.Trigger id="templateType" style={{ width: '100%' }} mt="1" />
              <Select.Content>
                {Object.entries(DEVICE_TYPE_LABELS).map(([value, label]) => (
                  <Select.Item key={value} value={value}>
                    <Flex align="center" gap="2">
                      <DeviceIcon type={value as DeviceType} size={16} />
                      <span>{label}</span>
                    </Flex>
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </Box>
        )}
      </Flex>

      <Flex gap="3">
        <Box style={{ flex: 1 }}>
          <Text as="label" size="2" weight="bold" htmlFor="templateBrand">
            Brand (optional)
          </Text>
          <TextField.Root
            id="templateBrand"
            value={template.brand || ''}
            onChange={(e) => onChange({ ...template, brand: e.target.value })}
            placeholder="e.g., ZWO"
            mt="1"
          />
        </Box>

        <Box style={{ flex: 1 }}>
          <Text as="label" size="2" weight="bold" htmlFor="templateModel">
            Model (optional)
          </Text>
          <TextField.Root
            id="templateModel"
            value={template.model || ''}
            onChange={(e) => onChange({ ...template, model: e.target.value })}
            placeholder="e.g., AM5N"
            mt="1"
          />
        </Box>
      </Flex>

      <Box>
        <Text as="label" size="2" weight="bold" htmlFor="templateDescription">
          Description (optional)
        </Text>
        <TextField.Root
          id="templateDescription"
          value={template.description || ''}
          onChange={(e) => onChange({ ...template, description: e.target.value })}
          placeholder="Additional information..."
          mt="1"
        />
      </Box>
    </>
  );
};

export default BasicInfoFields;
